export const BRAND = {
  name: "Indusynq",
  tagline: "Cash Flow Above All.",
  founder: "Vivek Kashyap",
  founderRole: "Founder & Principal Consultant",
  city: "Mumbai, India",
  phone: "+91 99027 44788",
  email: "Vivek.kashyap@indusynq.com",
  serviceEmail: "customer.service@indusynq.com",
  years: "30",
};

export const DOMAINS = [
  {
    key: "fcf",
    number: "01",
    title: "Free Cash Flow Optimisation",
    kicker: "The North-Star Metric",
    bullets: [
      "Cash Conversion Cycle (DSO + Inventory − DPO)",
      "Working-capital release, ageing rigor, DPO renegotiation",
      "Inventory buffer sizing at the constraint only",
    ],
    accent: "text-emerald-600",
    dot: "bg-emerald-500",
  },
  {
    key: "sales",
    number: "02",
    title: "Sales Effectiveness & Margin Health",
    kicker: "Revenue Without Velocity is Risk",
    bullets: [
      "Contribution margin by product & customer",
      "Deal quality, pricing leakage, quote-to-cash time",
      "Customer concentration and adjacency growth",
    ],
    accent: "text-violet-600",
    dot: "bg-violet-500",
  },
  {
    key: "supply",
    number: "03",
    title: "Supply Chain & OTIF Delivery",
    kicker: "Resilience Over Optimisation",
    bullets: [
      "Supplier lead-time variability, expedite cost",
      "Dual-source, buffer strategy, OTIF penalties",
      "Raw / WIP / Finished inventory health",
    ],
    accent: "text-amber-600",
    dot: "bg-amber-500",
  },
  {
    key: "mfg",
    number: "04",
    title: "Manufacturing Excellence",
    kicker: "Fix the One Constraint",
    bullets: [
      "Bottleneck OEE, scrap & yield loss",
      "SMED, micro-stop attack, standard work",
      "Drum-Buffer-Rope over local efficiency",
    ],
    accent: "text-sky-600",
    dot: "bg-sky-500",
  },
];

export const IMPACT_METRICS = [
  { value: "80%", label: "Metals aftermarket revenue growth", detail: "₹250 Cr → ₹450 Cr" },
  { value: "3.0×", label: "Enterprise business scaled", detail: "₹360 Cr → ₹1,500 Cr (Unicorn)" },
  { value: "39 → 85%", label: "OTIF delivery uplift", detail: "18 months, 2 plants" },
  { value: "₹850 Cr+", label: "Working capital released", detail: "Across engagements" },
  { value: "5%", label: "Direct-spend cost transform", detail: "On ₹900 Cr procurement" },
  { value: "92 → 98%", label: "First-Time-Right at plant", detail: "OEE Gemba program" },
];

export const CASE_STUDIES = [
  {
    id: "cs1",
    industry: "Metals — Heavy Engineering",
    role: "SVP, Metallurgical Services",
    challenge: "Metals aftermarket business was flatlining at ₹250 Cr with fragmented service revenue and OTIF collapsing below 40%.",
    intervention: [
      "Signed a 10 MTPA CCM Comprehensive Maintenance contract across two integrated steel sites.",
      "Established a greenfield workshop and brownfield capacity while transferring engineering from Japan, USA, Austria, UK, Germany.",
      "Expanded supplier ecosystem by 30%; scaled organisation from 150 → 190.",
      "Ran a plant-floor OEE program; First-Time-Right lifted from 92% → 98%.",
    ],
    outcomes: [
      { k: "Revenue", v: "₹250 Cr → ₹450 Cr" },
      { k: "OTIF", v: "39% → 85%" },
      { k: "Timeline", v: "24 months" },
    ],
    tradeoff: "Aftermarket investment slowed first-year EBITDA by ~2 pts, recovered by month 14 via maintenance-contract annuity.",
  },
  {
    id: "cs2",
    industry: "Industrial B2B — MRO & Distribution",
    role: "VP, Business Development & Supply Partnerships",
    challenge: "Enterprise business needed to scale from ₹360 Cr to ₹1,500 Cr with a supplier base misaligned to the digital procurement model.",
    intervention: [
      "Built a 200-member supply & category organisation across 14 city hubs.",
      "Consolidated MRO procurement, industrialised supplier onboarding.",
      "Lifted supplier alignment from 15% → 50%.",
      "Enabled global distribution of critical OC & PPE supplies across 20+ countries during pandemic.",
    ],
    outcomes: [
      { k: "Revenue", v: "3.0× (₹360 Cr → ₹1,500 Cr)" },
      { k: "Internal NPS", v: "+33%" },
      { k: "Milestone", v: "Contributed to Unicorn status" },
    ],
    tradeoff: "Rapid supplier consolidation temporarily narrowed sourcing options; hedged via dual-source of top-20 categories.",
  },
  {
    id: "cs3",
    industry: "Power Transmission — Bearings & Gear",
    role: "GM, Power Transmission Services BU",
    challenge: "Product-only P&L needed re-engineering into an integrated services business to grow customer lifetime value.",
    intervention: [
      "Built service-led business models integrating bearings, gear services, engineering support.",
      "90-member cross-functional team across 14 steel-mill service sites.",
      "Launched performance-linked service model at steel plants.",
    ],
    outcomes: [
      { k: "Revenue", v: "Doubled to ₹220 Cr" },
      { k: "Defense win", v: "₹24 Cr Indian Navy contract" },
      { k: "Service annuity", v: "₹80 Cr / year" },
    ],
    tradeoff: "Services required shifting talent from transactional sales into consultative engineering — 18-month retraining runway.",
  },
];

export const EXPERIENCE = [
  { years: "Jun 2026 — Present", role: "Founder & Principal Consultant", company: "Indusynq Consulting, Mumbai", note: "Boutique consulting for industrial manufacturing — Metals, Energy, Rail, Heavy Engineering, Process." },
  { years: "Apr 2023 — Apr 2026", role: "Senior Vice President — Metallurgical Services (P&L)", company: "Primetals Technologies (MHI Group)", note: "80% revenue growth, OTIF 39% → 85%, greenfield + global tech transfer." },
  { years: "Aug 2022 — Mar 2023", role: "Vice President — Metallurgical Services", company: "Primetals Technologies", note: "Promoted to SVP within 12 months." },
  { years: "Jan 2020 — May 2022", role: "VP — Business Development & Supply Partnerships", company: "Moglix", note: "Scaled enterprise 3× to ₹1,500 Cr; contributed to Unicorn status." },
  { years: "Apr 2016 — Dec 2019", role: "General Manager — Power Transmission Services (P&L)", company: "Timken India", note: "Doubled BU revenue to ₹220 Cr; secured Indian Navy contract." },
  { years: "Dec 2012 — Mar 2016", role: "GM — Supply Chain & AGM Strategic Sourcing", company: "Timken India", note: "₹1,700 Cr throughput; 5% cost transformation on ₹900 Cr spend." },
  { years: "Apr 2010 — Nov 2012", role: "Head — Marketing & New Business Development", company: "Timken India", note: "₹90 Cr aerospace contract with HAL." },
  { years: "Jul 2005 — Mar 2010", role: "National Sales Manager — Industrial & Rail", company: "Timken India", note: "Industrial OEM +67%, Rail +80%." },
  { years: "1997 — 2005", role: "Sales roles", company: "FAG Bearings (Schaeffler), Timken India", note: "Foundation in industrial sales & OEM engineering." },
];
